import React, { useEffect, useState } from 'react';
import { Table, Typography } from 'antd';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { formatTS } from '../utils/time';
import { HdStatusTag, helpdeskStatuses } from './HelpdeskStatus';

// Help Desk tickets a person is on as the Impacted Employee, as the one who
// raised it, or as a watcher (GET /api/helpdesk?involved_user_id= / mine=1), limited by the server to
// what the viewer may see. Used by My Hub > My HelpDesk, Agent Summary's
// HelpDesk tab and its To Do cards.

// A ticket's key: its queue's prefix and number (HDIT-325), set when it is made.
export const ticketKey = (t) => t.TicketKey || `HD-${t.ID}`;

// Every status not in the Done category (the server expands it).
export const HD_OPEN_STATUSES = ['@open'];

// userId null = the current user. involvedAs: any | impacted | creator | watcher.
export function fetchInvolvedTickets({ userId = null, involvedAs = 'any', statuses = null }) {
  const params = { involved_as: involvedAs };
  if (userId) params.involved_user_id = userId; else params.mine = 1;
  if (statuses?.length) params.status = statuses.join(',');
  return api.get('/api/helpdesk', { params }).then((r) => r.data || []);
}

const filtersOf = (rows, key) => [...new Set(rows.map((r) => r[key]).filter(Boolean))].sort()
  .map((v) => ({ text: v, value: v }));

// showEmployee: the ticket's Impacted Employee, for lists that include
// tickets the person only watches (whose they are is not obvious).
export function involvedTicketColumns(rows, { showRole = true, compact = false, showEmployee = false, employeeTitle = 'Impacted Employee' } = {}) {
  const strSort = (key) => (a, b) => (a[key] ?? '').localeCompare(b[key] ?? '');
  return [
    { title: 'Ticket', dataIndex: 'ID', key: 'ID', width: 100, sorter: (a, b) => a.ID - b.ID,
      render: (v, r) => <Typography.Text strong style={{ color: '#1677ff' }}>{ticketKey(r)}</Typography.Text> },
    { title: 'Status', dataIndex: 'Status', key: 'Status', width: 170, sorter: strSort('Status'),
      ...(compact ? {} : {
        filters: helpdeskStatuses().map((s) => ({ text: s.name, value: s.key })),
        onFilter: (v, r) => r.Status === v,
      }),
      render: (v) => <HdStatusTag status={v} /> },
    { title: 'Subject', dataIndex: 'Subject', key: 'Subject', sorter: strSort('Subject') },
    { title: 'Request Type', dataIndex: 'FormName', key: 'FormName', width: 190, sorter: strSort('FormName'),
      render: (v) => v || '—' },
    ...(showEmployee ? [{ title: employeeTitle, dataIndex: 'AgentName', key: 'AgentName', width: 180,
      sorter: strSort('AgentName'), render: (v) => v || '—' }] : []),
    ...(showRole ? [{ title: 'Role', dataIndex: 'Involvement', key: 'Involvement', width: 150,
      ...(compact ? {} : { filters: filtersOf(rows, 'Involvement'), onFilter: (v, r) => r.Involvement === v }) }] : []),
    // Not on the To Do cards (compact).
    ...(compact ? [] : [{ title: 'Assigned To', dataIndex: 'AssignedToName', key: 'AssignedToName', width: 160,
      sorter: strSort('AssignedToName'), render: (v) => v || '—' }]),
    { title: 'Created', dataIndex: 'CreatedAt', key: 'CreatedAt', width: 115, sorter: strSort('CreatedAt'),
      defaultSortOrder: 'descend', render: (v) => (v ? formatTS(v, 'YYYY-MM-DD') : '—') },
    ...(compact ? [] : [{ title: 'Last Change Date', dataIndex: 'UpdatedAt', key: 'UpdatedAt', width: 140,
      sorter: strSort('UpdatedAt'), render: (v) => (v ? formatTS(v, 'YYYY-MM-DD') : '—') }]),
  ];
}

// A self-loading table: every ticket (any status) the person is on, or with
// involvedAs="impacted" only those where they are the Impacted Employee.
export default function HelpdeskInvolvedTickets({ userId = null, involvedAs = 'any' }) {
  const navigate = useNavigate();
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    fetchInvolvedTickets({ userId, involvedAs })
      .then(setRows)
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [userId, involvedAs]);

  return (
    <Table
      rowKey="ID"
      size="small"
      loading={loading}
      dataSource={rows}
      columns={involvedTicketColumns(rows, { showRole: involvedAs === 'any', showEmployee: true })}
      // Always shown, with a size picker and a count: hiding it on a single page
      // also hid the picker once a bigger size was chosen.
      pagination={{ defaultPageSize: 25, size: 'small', showSizeChanger: true, pageSizeOptions: ['25', '50', '100', '200'],
        showTotal: (t) => `${t} ticket${t === 1 ? '' : 's'}` }}
      locale={{ emptyText: 'No tickets.' }}
      scroll={{ x: 'max-content' }}
      onRow={(r) => ({ onClick: () => navigate(`/helpdesk/${r.ID}`), style: { cursor: 'pointer' } })}
    />
  );
}
